// UserFormAdminScreen.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/Layout/DashboardLayout';
import TextInput from '../../components/FormElements/TextInput';
import PasswordInput from '../../components/FormElements/PasswordInput';
import SelectInput from '../../components/FormElements/SelectInput';
import AlertMessage from '../../components/ui/AlertMessage';
import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay';
import defaultUserProfileImage from '../../assets/img/user-profile.jpg';
import PencilFillIcon from '../../components/icons/PencilFillIcon';

const roleMappings = {
    admin: { plural: 'admins', singular: 'admin', translated: 'Administrador' },
    agent: { plural: 'agents', singular: 'agent', translated: 'Agente' },
    supplier: { plural: 'suppliers', singular: 'supplier', translated: 'Proveedor' },
    user: { plural: 'users', singular: 'user', translated: 'Usuario' },
};

const UserFormAdminScreen = () => {
    const { token } = useAuth();
    const { userIdParam, baseRoleKeyParam } = useParams();
    const navigate = useNavigate();
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        birthDate: '',
        country: '',
        // role: baseRoleKeyParam || '', // Role will not be editable from this form
        newPassword: '',
        repeatPassword: '',
        profileImageFile: null,
        profileImagePreview: defaultUserProfileImage,
        currentAvatarFileId: null,
    });

    const [message, setMessage] = useState({ type: '', text: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [pageTitle, setPageTitle] = useState('Editar Usuario');

    const countryOptions = [ // Reusing from ProfileUserScreen
        { value: "US", label: "Estados Unidos" },
        { value: "MX", label: "México" },
        { value: "ES", label: "España" },
        { value: "CL", label: "Chile" },
        { value: "CO", label: "Colombia" },
    ];

    // const roleOptions = Object.entries(roleMappings).map(([key, value]) => ({
    //     value: key,
    //     label: value.translated,
    // })); // Role selection removed

    const fetchUserData = useCallback(async () => {
        if (!userIdParam || !baseRoleKeyParam || !roleMappings[baseRoleKeyParam]) {
            setMessage({ type: 'danger', text: "Parámetros de usuario inválidos." });
            setIsLoadingData(false);
            return;
        }

        setIsLoadingData(true);
        setMessage({ type: '', text: '' });
        // setPageTitle(`Editar ${roleMappings[baseRoleKeyParam]?.translated || 'Usuario'}`); // Updated below

        try {
            const endpoint = `${API_BASE_URL}/api/${roleMappings[baseRoleKeyParam].plural}/${userIdParam}`;
            const response = await fetch(endpoint, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: `Error ${response.status}` }));
                throw new Error(errorData.message || `No se pudo cargar la información del usuario.`);
            }

            const userData = await response.json();

            let determinedProfileImage = defaultUserProfileImage;
            if (userData.avatar_file_id) {
                determinedProfileImage = `${API_BASE_URL}/files/serve/${userData.avatar_file_id}`;
            } else if (userData.image && typeof userData.image === 'string' && userData.image.startsWith('http')) {
                determinedProfileImage = userData.image;
            }

            const userNameForTitle = userData.name || userData.username || 'Usuario';
            const userRoleForTitle = roleMappings[baseRoleKeyParam]?.translated || baseRoleKeyParam;
            setPageTitle(`Editar ${userNameForTitle} (${userRoleForTitle})`);

            setFormData({
                name: userData.name || userData.username || '',
                email: userData.email || '',
                birthDate: userData.birth_date ? userData.birth_date.split('T')[0] : '',
                country: userData.country || '',
                // role: baseRoleKeyParam, // Role is fixed based on baseRoleKeyParam
                newPassword: '',
                repeatPassword: '',
                profileImageFile: null,
                profileImagePreview: determinedProfileImage,
                currentAvatarFileId: userData.avatar_file_id || null,
            });

        } catch (error) {
            console.error("Error fetching user data:", error);
            setMessage({ type: 'danger', text: error.message });
        } finally {
            setIsLoadingData(false);
        }
    }, [userIdParam, baseRoleKeyParam, token, API_BASE_URL]);

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    useEffect(() => {
        const currentPreview = formData.profileImagePreview;
        if (currentPreview && currentPreview.startsWith('blob:')) {
            return () => {
                URL.revokeObjectURL(currentPreview);
            };
        }
    }, [formData.profileImagePreview]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setFormData(prev => ({
                ...prev,
                profileImageFile: file,
                profileImagePreview: URL.createObjectURL(file)
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        if (formData.newPassword && formData.newPassword !== formData.repeatPassword) {
            setMessage({ type: 'danger', text: 'Las nuevas contraseñas no coinciden.' });
            return;
        }

        if (!roleMappings[baseRoleKeyParam]) {
            setMessage({ type: 'danger', text: 'Tipo de rol base inválido.' });
            return;
        }

        setIsSubmitting(true);

        try {
            let newUploadedAvatarFileId = null;

            if (formData.profileImageFile) {
                const imageFormData = new FormData();
                imageFormData.append('avatar', formData.profileImageFile);

                const imageUploadResponse = await fetch(`${API_BASE_URL}/api/files/upload/avatar`, {
                    method: 'POST',
                    body: imageFormData,
                    headers: { 'Authorization': `Bearer ${token}` },
                });

                if (!imageUploadResponse.ok) {
                    const errorData = await imageUploadResponse.json().catch(() => ({ message: 'Error al subir la imagen.' }));
                    throw new Error(errorData.message || `Error al subir la imagen: ${imageUploadResponse.statusText}`);
                }
                const uploadResult = await imageUploadResponse.json();
                newUploadedAvatarFileId = uploadResult.file_id;
            }

            const userUpdatePayload = {
                name: formData.name, // Assuming API expects 'name', adjust if it's 'username'
                email: formData.email,
                birth_date: formData.birthDate,
                country: formData.country,
                // role: formData.role, // Role is not part of the update payload anymore
            };

            if (formData.newPassword) {
                userUpdatePayload.password = formData.newPassword;
            }

            if (newUploadedAvatarFileId) {
                userUpdatePayload.avatar_file_id = newUploadedAvatarFileId;
            } else if (formData.currentAvatarFileId && !formData.profileImageFile) {
                // Keep existing avatar if no new one is uploaded
                userUpdatePayload.avatar_file_id = formData.currentAvatarFileId;
            } else if (!newUploadedAvatarFileId && !formData.currentAvatarFileId) {
                // No new image and no existing avatar_file_id, explicitly set to null
                userUpdatePayload.avatar_file_id = null;
            }

            const updateEndpoint = `${API_BASE_URL}/api/${roleMappings[baseRoleKeyParam].plural}/${userIdParam}`;
            const userUpdateResponse = await fetch(updateEndpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(userUpdatePayload),
            });

            if (!userUpdateResponse.ok) {
                const errorData = await userUpdateResponse.json().catch(() => ({ message: 'Error al actualizar el perfil.' }));
                throw new Error(errorData.message || `Error al actualizar el perfil: ${userUpdateResponse.statusText}`);
            }

            // const updatedUserFromServer = await userUpdateResponse.json(); // Use if needed

            setMessage({ type: 'success', text: 'Usuario actualizado con éxito.' });
            // Optionally, refetch data or navigate away
            // fetchUserData(); // To refresh form with latest data if staying
            // navigate('/admin/users'); // To go back to list

            // For immediate UI feedback if staying on page and image changed:
            if (newUploadedAvatarFileId) {
                setFormData(prev => ({
                    ...prev,
                    profileImagePreview: `${API_BASE_URL}/files/serve/${newUploadedAvatarFileId}`,
                    profileImageFile: null,
                    currentAvatarFileId: newUploadedAvatarFileId,
                }));
            } else if (formData.profileImageFile && !newUploadedAvatarFileId) {
                // Image upload failed but was attempted, revert preview if needed or rely on error message
            }
            if (formData.newPassword) {
                setFormData(prev => ({ ...prev, newPassword: '', repeatPassword: '' }));
            }


        } catch (error) {
            console.error("Error submitting profile update:", error);
            setMessage({ type: 'danger', text: error.message || 'Error al guardar los cambios.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoadingData) {
        return <FormSpinnerOverlay isLoading={true} />;
    }

    return (
        <DashboardLayout activeMenuItem="Usuarios" pageTitle={pageTitle}>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3">
                <h1 className="h2 mb-0">{pageTitle}</h1>
                <button className="btn btn-outline-secondary" onClick={() => navigate('/users')}>
                    Volver a la lista
                </button>
            </div>
            <div className="row text-start mb-4">
                <div className="col-12">
                    <div className="card card-metric border-0">
                        <FormSpinnerOverlay isLoading={isSubmitting} />
                        <div className="card-body">
                            {message.text && <AlertMessage type={message.type} message={message.text} />}
                            <form id="userEditForm" onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-md-3 text-center mb-3 mb-md-0">
                                        <div className="position-relative mx-auto" style={{ width: '150px', height: '150px' }}>
                                            <img
                                                src={formData.profileImagePreview || defaultUserProfileImage}
                                                alt="Avatar"
                                                className="img-fluid rounded-circle shadow-sm"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                            <label htmlFor="profileImageInput" className="btn btn-sm btn-checkin position-absolute p-2 rounded-circle" style={{ bottom: '5px', right: '5px', cursor: 'pointer', lineHeight: 1, boxShadow: '0 0 5px rgba(0,0,0,0.2)' }} title="Editar imagen">
                                                <PencilFillIcon />
                                                <span className="visually-hidden">Editar imagen</span>
                                            </label>
                                            <input type="file" id="profileImageInput" name="profileImage" className="d-none" accept="image/*" onChange={handleImageChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-9">
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <TextInput
                                                    label="Nombre"
                                                    id="userName"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <TextInput
                                                    label="Email"
                                                    id="userEmail"
                                                    name="email"
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <TextInput
                                                    label="Fecha de nacimiento"
                                                    id="birthDate"
                                                    name="birthDate"
                                                    type="date"
                                                    value={formData.birthDate}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <SelectInput
                                                    label="País de origen"
                                                    id="country"
                                                    name="country"
                                                    value={formData.country}
                                                    onChange={handleChange}
                                                    options={countryOptions}
                                                    defaultOptionText="Selecciona un país..."
                                                />
                                            </div>
                                            {/* Role Select Input Removed */}
                                            {/* <div className="col-md-6 mb-3">
                                                <SelectInput
                                                    label="Rol"
                                                    id="role"
                                                    name="role"
                                                    value={formData.role}
                                                    onChange={handleChange}
                                                    options={roleOptions}
                                                    required
                                                />
                                            </div> */}
                                        </div>
                                    </div>
                                </div>
                                <hr className="my-4" />
                                <h5 className="mb-3">Cambiar Contraseña (opcional)</h5>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <PasswordInput
                                            label="Nueva contraseña"
                                            id="newPassword"
                                            name="newPassword"
                                            value={formData.newPassword}
                                            onChange={handleChange}
                                            autoComplete="new-password"
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <PasswordInput
                                            label="Repita la nueva contraseña"
                                            id="repeatPassword"
                                            name="repeatPassword"
                                            value={formData.repeatPassword}
                                            onChange={handleChange}
                                            autoComplete="new-password"
                                        />
                                    </div>
                                </div>
                                <div className="row mt-4">
                                    <div className="col-12 text-end">
                                        <button type="submit" className="btn btn-checkin px-4" disabled={isSubmitting}>
                                            {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default UserFormAdminScreen;
